import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui';
import { Skeleton } from '@/components/ui/states';
import { AppLayout } from './layout/app-layout';
import { ProtectedRoute } from './protected-route';
import { PortalLayout } from './layout/portal-layout';
import { SinPortal } from './guardia-de-portal';

// Carga diferida por ruta: el bundle inicial no arrastra pantallas que el
// usuario quizá no visite (plan.md §33.2).
const PanelPage = lazy(() => import('@/pages/PanelPage'));
const ObrasPage = lazy(() => import('@/pages/ObrasPage'));
const ObraDetallePage = lazy(() => import('@/pages/ObraDetallePage'));
const AccesoPage = lazy(() => import('@/pages/AccesoPage'));
const RecuperarPage = lazy(() => import('@/pages/RecuperarPage'));
const EstablecerPasswordPage = lazy(() => import('@/pages/EstablecerPasswordPage'));
const UsuariosPage = lazy(() => import('@/pages/UsuariosPage'));
const PapeleraPage = lazy(() => import('@/pages/PapeleraPage'));
const FavoritosPage = lazy(() => import('@/pages/FavoritosPage'));
const EstadoPage = lazy(() => import('@/pages/EstadoPage'));
const DisenoPage = lazy(() => import('@/pages/DisenoPage'));
const PendientePage = lazy(() => import('@/pages/PendientePage'));
const ClientesPage = lazy(() => import('@/pages/ClientesPage'));
const IncidenciasPage = lazy(() => import('@/pages/IncidenciasPage'));
const FotosPage = lazy(() => import('@/pages/FotosPage'));
const TareasPage = lazy(() => import('@/pages/TareasPage'));
const MisTareasPage = lazy(() => import('@/pages/MisTareasPage'));
const TareasDeveloperPage = lazy(() => import('@/pages/TareasDeveloperPage'));
const DocumentosPage = lazy(() => import('@/pages/DocumentosPage'));
const AuditoriaPage = lazy(() => import('@/pages/AuditoriaPage'));
const BuscarPage = lazy(() => import('@/pages/BuscarPage'));
const ClienteDetallePage = lazy(() => import('@/pages/ClienteDetallePage'));
const CompartidoPage = lazy(() => import('@/pages/CompartidoPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const PortalObrasPage = lazy(() => import('@/pages/PortalObrasPage'));
const PortalObraPage = lazy(() => import('@/pages/PortalObraPage'));
const ProveedoresPage = lazy(() => import('@/pages/ProveedoresPage'));
const ProveedorDetallePage = lazy(() => import('@/pages/ProveedorDetallePage'));
const EmpleadosPage = lazy(() => import('@/pages/EmpleadosPage'));
const EmpleadoDetallePage = lazy(() => import('@/pages/EmpleadoDetallePage'));
const MaquinariaPage = lazy(() => import('@/pages/MaquinariaPage'));
const MaquinaDetallePage = lazy(() => import('@/pages/MaquinaDetallePage'));
const VencimientosPage = lazy(() => import('@/pages/VencimientosPage'));
const AnaliticaPage = lazy(() => import('@/pages/AnaliticaPage'));
const CalendarioPage = lazy(() => import('@/pages/CalendarioPage'));
const ComercialPage = lazy(() => import('@/pages/ComercialPage'));
const PresupuestosPage = lazy(() => import('@/pages/PresupuestosPage'));
const PresupuestoDetallePage = lazy(() => import('@/pages/PresupuestoDetallePage'));
const CertificacionesPage = lazy(() => import('@/pages/CertificacionesPage'));
const CertificacionDetallePage = lazy(() => import('@/pages/CertificacionDetallePage'));
const AyudaPage = lazy(() => import('@/pages/AyudaPage'));

function PageFallback() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/** Secciones ya navegables pero aún sin construir (ver PendientePage). */
const PLACEHOLDERS = ['perfil', 'mas', 'equipo'];

export function AppRouter() {
  return (
    <BrowserRouter>
      <TooltipProvider delayDuration={400}>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Fuera de la estructura de la aplicación */}
            <Route path="/acceso" element={<AccesoPage />} />
            <Route path="/recuperar" element={<RecuperarPage />} />
            <Route path="/invitacion/:token" element={<EstablecerPasswordPage />} />
            <Route path="/restablecer/:token" element={<EstablecerPasswordPage />} />

            {/* Enlace público. VA FUERA DE `ProtectedRoute` a propósito: quien
                lo abre no tiene cuenta, y mandarlo a /acceso sería un callejón
                sin salida. */}
            <Route path="/s/:token" element={<CompartidoPage />} />

            <Route element={<ProtectedRoute />}>
              {/* EL PORTAL DEL CLIENTE — tarea 8.3.
                  Va ANTES que la aplicación y con su propio armazón: el menú
                  lateral de `AppLayout` es una lista de sitios donde el cliente
                  recibiría un 404. Ver `guardia-de-portal.tsx`. */}
              <Route element={<PortalLayout />}>
                <Route path="/portal" element={<PortalObrasPage />} />
                <Route path="/portal/obras/:id" element={<PortalObraPage />} />
              </Route>

              <Route element={<SinPortal />}>
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Navigate to="/panel" replace />} />
                  <Route path="/panel" element={<PanelPage />} />
                  <Route path="/obras" element={<ObrasPage />} />
                  <Route path="/obras/:id" element={<ObraDetallePage />} />
                  <Route path="/documentos" element={<DocumentosPage />} />
                  <Route path="/buscar" element={<BuscarPage />} />
                  <Route path="/clientes" element={<ClientesPage />} />
                  <Route path="/incidencias" element={<IncidenciasPage />} />
                  <Route path="/fotos" element={<FotosPage />} />
                  <Route path="/tareas" element={<TareasPage />} />
                  <Route path="/mis-tareas" element={<MisTareasPage />} />

                  {/* Pantalla interna del desarrollo: no está en el menú a
                    propósito. Ver TareasDeveloperPage. */}
                  <Route path="/tareas-developer" element={<TareasDeveloperPage />} />
                  <Route path="/clientes/:id" element={<ClienteDetallePage />} />
                  <Route path="/proveedores" element={<ProveedoresPage />} />
                  <Route path="/proveedores/:id" element={<ProveedorDetallePage />} />
                  <Route path="/empleados" element={<EmpleadosPage />} />
                  <Route path="/empleados/:id" element={<EmpleadoDetallePage />} />
                  <Route path="/maquinaria" element={<MaquinariaPage />} />
                  <Route path="/maquinaria/:id" element={<MaquinaDetallePage />} />
                  <Route path="/vencimientos" element={<VencimientosPage />} />
                  <Route path="/analitica" element={<AnaliticaPage />} />
                  <Route path="/calendario" element={<CalendarioPage />} />
                  <Route path="/comercial" element={<ComercialPage />} />
                  <Route path="/presupuestos" element={<PresupuestosPage />} />
                  <Route path="/presupuestos/:id" element={<PresupuestoDetallePage />} />
                  <Route path="/certificaciones" element={<CertificacionesPage />} />
                  <Route path="/certificaciones/:id" element={<CertificacionDetallePage />} />
                  <Route path="/usuarios" element={<UsuariosPage />} />
                  <Route path="/auditoria" element={<AuditoriaPage />} />
                  <Route path="/papelera" element={<PapeleraPage />} />
                  <Route path="/favoritos" element={<FavoritosPage />} />
                  <Route path="/ayuda" element={<AyudaPage />} />
                  <Route path="/estado" element={<EstadoPage />} />
                  <Route path="/diseno" element={<DisenoPage />} />

                  {PLACEHOLDERS.map((path) => (
                    <Route key={path} path={`/${path}`} element={<PendientePage />} />
                  ))}

                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </TooltipProvider>
    </BrowserRouter>
  );
}
